import {
  Avatar,
  Box,
  Container,
  Tab,
  Tabs,
  Typography,
  Breadcrumbs,
  Link,
} from '@mui/material'
import React, { ReactElement, useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  fetchClasses,
  fetchCourses,
  fetchTitleScreens,
  fetchVideos,
  fetchTextContent,
  fetchQuizzes,
  fetchStoryboardSteps,
  selectClassesStatus,
  selectCoursesStatus,
  selectTitleScreensStatus,
  selectVideosStatus,
  selectTextContentStatus,
  selectQuizzesStatus,
  selectStoryboardStepsStatus,
  selectClassByID,
  selectCourseByID,
  activeClassChanged,
} from 'src/v3/create/create.slice'
import Loading from 'src/v3/components/loading'
import BasicInfo from './basic-info'
import TitleScreen from './title-screen'
import Content from './content'
import Quiz from './quiz'
import Storyboard from './storyboard'
import Preview from './preview'

type ClassStep =
  | 'basic-info'
  | 'title-screen'
  | 'content'
  | 'quiz'
  | 'storyboard'
  | 'preview'

export default (): ReactElement => {
  let dispatch = useAppDispatch()
  let navigate = useNavigate()
  let { courseId, classId } = useParams()

  let selectedClass = useAppSelector(selectClassByID(classId || ''))
  let course = useAppSelector(selectCourseByID(courseId || ''))
  let classesStatus = useAppSelector(selectClassesStatus)
  let coursesStatus = useAppSelector(selectCoursesStatus)
  let titleScreensStatus = useAppSelector(selectTitleScreensStatus)
  let videosStatus = useAppSelector(selectVideosStatus)
  let textContentStatus = useAppSelector(selectTextContentStatus)
  let quizzesStatus = useAppSelector(selectQuizzesStatus)
  let storyboardStepsStatus = useAppSelector(selectStoryboardStepsStatus)

  let [step, setStep] = useState<ClassStep>('basic-info')
  let [createdClass, setCreatedClass] = useState<any>(null) // Track created class
  let isEditing = !!classId && !!selectedClass
  let currentClass = selectedClass || createdClass // Use either URL class or created class
  let canNavigateToOtherSteps = isEditing || !!currentClass // Allow navigation after basic-info is completed

  // Handle class creation callback
  let handleClassCreated = (newClass: any) => {
    setCreatedClass(newClass)
    // Set the active class in Redux so title screens are stored/retrieved correctly
    dispatch(activeClassChanged(newClass.id))
    setStep('title-screen')
  }

  // Handle finish - return to course classes step
  let handleFinish = () => {
    if (courseId) {
      navigate(`/create/v4/course/${courseId}`)
    } else {
      navigate('/create')
    }
  }

  useEffect(() => {
    if (classesStatus === 'uninitialized') {
      dispatch(fetchClasses())
    }
  }, [classesStatus])

  // Set active class when editing an existing class
  useEffect(() => {
    if (classId && selectedClass) {
      dispatch(activeClassChanged(classId))
    }
  }, [classId, selectedClass])

  useEffect(() => {
    if (coursesStatus === 'uninitialized') {
      dispatch(fetchCourses())
    }
  }, [coursesStatus])

  useEffect(() => {
    if (titleScreensStatus === 'uninitialized' && classId) {
      dispatch(fetchTitleScreens(classId))
    }
  }, [titleScreensStatus, classId])

  useEffect(() => {
    if (videosStatus === 'uninitialized') {
      dispatch(fetchVideos())
    }
  }, [videosStatus])

  useEffect(() => {
    if (textContentStatus === 'uninitialized' && classId) {
      dispatch(fetchTextContent(classId))
    }
  }, [textContentStatus, classId])

  useEffect(() => {
    if (quizzesStatus === 'uninitialized' && classId) {
      dispatch(fetchQuizzes(classId))
    }
  }, [quizzesStatus, classId])

  useEffect(() => {
    if (
      storyboardStepsStatus === 'uninitialized' &&
      (classId || currentClass?.id)
    ) {
      dispatch(fetchStoryboardSteps())
    }
  }, [storyboardStepsStatus, classId, currentClass?.id])

  let tabConfig = [
    { step: 'basic-info', label: 'Basic Info' },
    { step: 'title-screen', label: 'Title Screen' },
    { step: 'content', label: 'Content' },
    { step: 'quiz', label: 'Quiz' },
    { step: 'storyboard', label: 'Storyboard' },
    { step: 'preview', label: 'Preview' },
  ] as const

  return (
    <Container maxWidth="xl">
      {/* Tab Navigation */}
      <Tabs
        variant="fullWidth"
        value={step}
        onChange={
          canNavigateToOtherSteps ? (_, value) => setStep(value) : undefined
        }
      >
        {tabConfig.map((tab, i) => (
          <Tab
            key={tab.step}
            value={tab.step}
            label={tab.label}
            icon={
              <Avatar
                sx={{
                  width: 24,
                  height: 24,
                  fontSize: 16,
                  color: step === tab.step ? undefined : 'text.secondary',
                  bgcolor:
                    step === tab.step ? 'primary.main' : 'background.paper',
                }}
              >
                {i + 1}
              </Avatar>
            }
            iconPosition="start"
            sx={{
              my: -1,
              cursor: canNavigateToOtherSteps ? 'pointer' : 'auto',
              textTransform: 'none',
            }}
          />
        ))}
      </Tabs>

      {/* Breadcrumbs */}
      {course && (
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link
            component="button"
            variant="body2"
            onClick={() => navigate(`/create/v4/course/${courseId}`)}
            sx={{ textDecoration: 'none' }}
          >
            {course.name}
          </Link>
          <Typography variant="body2" color="text.primary">
            {isEditing
              ? `Edit Class: ${currentClass?.name}`
              : 'Create New Class'}
          </Typography>
        </Breadcrumbs>
      )}

      {/* Header */}
      <Box sx={{ my: 5, display: 'flex', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h2" sx={{ fontWeight: 700, mb: 0.5 }}>
            {isEditing
              ? `Edit Class: ${currentClass?.name}`
              : 'Create New Class'}
          </Typography>
          <Typography variant="subtitle1" color="textSecondary">
            {course
              ? `for ${course.name}`
              : 'Set up class content and structure'}
          </Typography>
        </Box>
      </Box>

      {/* Step Content */}
      {step === 'basic-info' && (
        <BasicInfo
          selectedClass={currentClass}
          course={course}
          setStep={setStep}
          onClassCreated={handleClassCreated}
        />
      )}
      {step === 'title-screen' && (
        <TitleScreen selectedClass={currentClass} setStep={setStep} />
      )}
      {step === 'content' && (
        <Content selectedClass={currentClass} setStep={setStep} />
      )}
      {step === 'quiz' && (
        <Quiz selectedClass={currentClass} setStep={setStep} />
      )}
      {step === 'storyboard' && (
        <Storyboard selectedClass={currentClass} setStep={setStep} />
      )}
      {step === 'preview' && (
        <Preview
          selectedClass={currentClass}
          setStep={setStep}
          onFinish={handleFinish}
        />
      )}

      <Loading
        entities={[
          classesStatus,
          coursesStatus,
          videosStatus,
          // Only include class-specific statuses when editing an existing class
          ...(classId
            ? [
                titleScreensStatus,
                textContentStatus,
                quizzesStatus,
                storyboardStepsStatus,
              ]
            : []),
        ]}
      />
    </Container>
  )
}
